import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import BreadCrumb from 'components/BreadCrumb'
import DefaultButton from 'components/DefaultButton'
import DefaultCreationForm, {
	DefaultCreationFormButtonGroup,
	DefaultCreationFormGroup,
} from 'components/DefaultCreationForm'
import DefaultInput from 'components/DefaultInput'
import { DefaultPageTitle } from 'components/DefaultPageTitle'
import { CreateAndEditCouponContainer } from './style'
import checkEmptyString from 'helpers/check-empty-string'
import { getCoupon, createCoupon, updateCoupon } from 'services/coupons'
import { COUPONS } from 'components/Routes/Constants'

import showErrorMessage from 'helpers/show-error-message'

interface CreateAndEditCouponProps {
	couponId: string
}

const CreateAndEditCoupon: React.FC = () => {
	const { couponId } = useParams<CreateAndEditCouponProps>()

	const history = useHistory()

	const [name, setName] = useState('')
	const [speciality, setSpeciality] = useState('')
	const [couponCode, setCouponCode] = useState('')

	const createContent = async (event: React.FormEvent) => {
		event.preventDefault()

		try {
			if (checkEmptyString(name)) {
				throw new Error('Informe um nome válido para o profissional')
			}

			if (checkEmptyString(speciality)) {
				throw new Error('Informe uma especialidade válida para o profissional')
			}

			if (checkEmptyString(couponCode)) {
				throw new Error('Informe um código de cupom válido')
			}

			await createCoupon({
				name,
				speciality: speciality ?? '',
				coupon_code: couponCode,
			})

			Swal.fire({
				title: 'Sucesso!',
				text: 'Cupom criado com sucesso!',
				icon: 'success',
			})

			goToCoupons()
		} catch (error: any) {
			var errorMessage = 'Houve um erro ao criar o produto. Tente novamente';
			if (error.response && error.response.data.message) {
				errorMessage = error.response.data.message;
			}

			Swal.fire({
				title: 'Erro',
				text: errorMessage,
				icon: 'error',
			})
		}
	}

	const updateContent = async (event: React.FormEvent) => {
		event.preventDefault()

		try {
			if (checkEmptyString(name)) {
				throw new Error('Informe um nome válido para o profissional')
			}

			if (checkEmptyString(speciality)) {
				throw new Error('Informe uma especialidade válida para o profissional')
			}

			if (checkEmptyString(couponCode)) {
				throw new Error('Informe um código de cupom válido')
			}

			await updateCoupon(couponId, {
				name,
				speciality: speciality ?? '',
				coupon_code: couponCode,
			})

			Swal.fire({
				title: 'Sucesso!',
				text: 'Cupom atualizado com sucesso!',
				icon: 'success',
			})

			goToCoupons()
		} catch (error) {
			showErrorMessage(error)
		}
	}

	const goToCoupons = () => {
		history.push(`${COUPONS}`)
	}

	const getContent = useCallback(async () => {
		if (couponId) {
			const coupon = await getCoupon(couponId)
			if (coupon && Object.keys(coupon).length) {
				setName(coupon.name)
				setSpeciality(coupon.speciality!)
				setCouponCode(coupon.coupon_code)
			}
		}
	}, [couponId])

	useEffect(() => {
		getContent()
	}, [getContent])

	const isEditting = useMemo(() => !!couponId, [couponId])

	return (
		<CreateAndEditCouponContainer>
			<BreadCrumb
				crumbs={[<Link to={`${COUPONS}`}>Cupons</Link>, <span>{isEditting ? 'Editar' : 'Criar'} Cupom</span>]}
			/>

			<DefaultPageTitle>{isEditting ? 'Editar' : 'Criar'} Produto</DefaultPageTitle>

			<DefaultCreationForm>
				<DefaultCreationFormGroup>
					<label className="required" htmlFor="name">
						Nome do profissional
					</label>
					<DefaultInput value={name} onChange={(e) => setName(e.target.value)} id="name" required />
				</DefaultCreationFormGroup>

				<DefaultCreationFormGroup>
					<label htmlFor="speciality">Especialidade do profissional</label>
					<DefaultInput value={speciality} onChange={(e) => setSpeciality(e.target.value)} id="speciality" />
				</DefaultCreationFormGroup>

				<DefaultCreationFormGroup>
					<label className="required" htmlFor="coupon-code">
						Código do cupom
					</label>
					<DefaultInput
						value={couponCode}
						onChange={(e) => setCouponCode(e.target.value)}
						id="coupon-code"
						required
					/>
				</DefaultCreationFormGroup>

				<DefaultCreationFormButtonGroup>
					<DefaultButton type="button" className="danger" onClick={goToCoupons}>
						Cancelar
					</DefaultButton>
					<DefaultButton
						onClick={(e) => (isEditting ? updateContent(e) : createContent(e))}
						className="success"
					>
						Salvar
					</DefaultButton>
				</DefaultCreationFormButtonGroup>
			</DefaultCreationForm>
		</CreateAndEditCouponContainer>
	)
}

export default CreateAndEditCoupon
